/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { IconDownload, IconEye, IconSearch, IconX } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter";
import NormalSearch from "../../../components/common/NormalSearch";
import ListView from "../../../components/common/ListView";
import RequestViewApproveModal from "./RequestViewApproveModal";
import { useAllLinehaulRequestFetchData } from "../../../apis/fleetApis";
import formatDate from "../../../utilities/helper";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { handleDownload } from "../../../utilities/downloadHelper";
import { useLanguage } from "../../../context/LanguageContext";

type LineHaulRequest = {
  id: string;
};

interface Props {
  setHeaderButtons?: (node: React.ReactNode) => void;
}

const LinehualTab: React.FC<Props> = ({ setHeaderButtons }) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [pageNo, setPageNo] = useState<number>(1);

  useEffect(() => {
    if (setHeaderButtons) {
      setHeaderButtons(null);
    }
    return () => {
      if (setHeaderButtons) {
        setHeaderButtons(null);
      }
    };
  }, [setHeaderButtons]);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<any>("");
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<LineHaulRequest[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const openModal = (row: any) => {
    setSelectedRequest(row);
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
    setSelectedRequest(null);
  };

  const { data, refetch, isLoading, isFetching } =
    useAllLinehaulRequestFetchData(pageNo, limit, searchFor);
  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (data?.results) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item: any) =>
          selectedRows.includes(item.id)
        );
        const newSelected = data.results.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id)
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.results, selectedRows]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit]);

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Request ID", value: "requestId", isShow: true },
    { text: "Email", value: "email", isShow: true },
    { text: "Contact Person", value: "contactPerson", isShow: true },
    { text: "Mobile Number", value: "mobile", isShow: true },
    { text: "Origin", value: "origin", isShow: true },
    { text: "Destination", value: "destination", isShow: true },
    { text: "Company Name", value: "companyName", isShow: true },
    { text: "Amount", value: "amount", isShow: true },
    { text: "Receive Date", value: "receiveDate", isShow: true },
    { text: "Response", value: "response", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col
      )
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.results?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.results.some((c: any) => c.id === id))
      );
    } else {
      const newSelections =
        data?.results
          ?.filter((c: any) => !selectedRows.includes(c.id))
          .map((c: any) => c.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.results?.length > 0 &&
                    data?.results?.every((c: any) =>
                      selectedRows.includes(c.id)
                    )
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                 bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                  top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (c: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(c.id)}
                  onChange={() => handleSelectRow(c.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: t("S/N"),
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "requestId":
          return {
            label: t("Request ID"),
            render: (c: any) => c.requestId || "",
          };
        case "email":
          return {
            label: t("Email"),
            render: (c: any) => c.fromEmail || "",
          };
        case "contactPerson":
          return {
            label: t("Contact Person"),
            render: (c: any) => c.assignedCompanyPerson || "",
          };
        case "mobile":
          return {
            label: t("Mobile Number"),
            render: (c: any) => c.assignedCompanyMobile || "",
          };
        case "origin":
          return {
            label: t("Origin"),
            render: (c: any) => c.originWarehouse || "",
          };
        case "destination":
          return {
            label: t("Destination"),
            render: (c: any) => c.destinationWarehouse || "",
          };
        case "companyName":
          return {
            label: t("Company Name"),
            render: (c: any) => c.assignedCompany || "",
          };
        case "amount":
          return {
            label: t("Amount"),
            render: (c: any) => (
              <div className="text-[#212121] flex items-center space-x-1">
                <img
                  alt="Riyal"
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Unicode_0x20C1.svg/120px-Unicode_0x20C1.svg.png"
                  className="w-4"
                />
                <span>{c.amount || "00"}</span>
              </div>
            ),
          };
        case "receiveDate":
          return {
            label: t("Receive Date"),
            render: (c: any) => formatDate(c.created_at || ""),
          };
        case "response":
          return {
            label: t("Response"),
            render: (c: any) => (
              <button className="bg-[#ffffff] border border-[#d2d2d2] px-2 py-1 rounded-full">
                {c.reqFormCount || "0"} {t("Quotation")}
              </button>
            ),
          };
        case "status":
          return {
            label: t("Status"),
            render: (c: any) => {
              const isAssigned = c.status === "APPROVE";
              const statusClasses = isAssigned
                ? "bg-[#E6F7E8] border border-[#20BF55] text-[#212121]"
                : "bg-[#FBD037]/10 border border-[#FBD037] text-[#212121]";
              return (
                <div className="flex justify-center">
                  <button
                    className={`rounded-full w-24 px-4 text-xs font-medium py-1.5 text-center ${statusClasses}`}
                  >
                    {t(c.status)}
                  </button>
                </div>
              );
            },
          };

        case "actions":
          return {
            label: t("Actions"),
            render: (c: any) => (
              <div className="flex justify-center items-center">
                <button
                  onClick={() => openModal(c)}
                  className="p-2 border rounded-full bg-[#F7F6F2]"
                >
                  <IconEye size={22} />
                </button>
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    [t("S/N")]: index + 1,
    [t("Request ID")]: c.requestId,
    [t("Email")]: c.fromEmail,
    [t("Contact Person")]: c.assignedCompanyPerson,
    [t("Mobile Number")]: c.assignedCompanyMobile,
    [t("Origin")]: c.originWarehouse,
    [t("Destination")]: c.destinationWarehouse,
    [t("Company Name")]: c.assignedCompany,
    [t("Amount")]: c.amount,
    [t("Receive Date")]: formatDate(c.created_at),
    [t("Response")]: c.reqFormCount,
    [t("Status")]: t(c.status),
  });

  return (
    <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-tr-3xl rounded-b-3xl w-full">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

      <div className="col-span-12 space-y-4 min-w-0">
        <div className="bg-white rounded-3xl overflow-visible mb-6">
          {/* Header Row (Filters + Actions Unified) */}
          <div className="px-6 py-3.5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            
            {/* Left side: Empty / removed badge */}
            <div className="flex flex-wrap items-center gap-3"></div>

            {/* Right side: Export + Search + Advanced Filters */}
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Sliding Search */}
              <div className="relative flex items-center">
                <div
                  onClick={() => {
                    if (!showSearch) {
                      setShowSearch(true);
                    }
                  }}
                  className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                    showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                  }`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSearch(!showSearch);
                    }}
                    className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    title={t("Search")}
                  >
                    <IconSearch size={17} stroke={2.5} />
                  </button>
                  {showSearch && (
                    <>
                      <input
                        type="text"
                        value={searchFor}
                        onChange={(e) => {
                          setSearchFor(e.target.value);
                          setPageNo(1);
                        }}
                        placeholder={t("Search...")}
                        autoFocus
                        className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchFor("");
                          setShowSearch(false);
                        }}
                        className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                        title={t("Clear & Close")}
                      >
                        <IconX size={14} stroke={2.5} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Export */}
              <button
                onClick={() =>
                  handleDownload({
                    selectedRows,
                    allSelectedData,
                    setSelectedRows,
                    setAllSelectedData,
                    showCheckboxes,
                    setShowCheckboxes,
                    setColumnsData,
                    mapDataToExport,
                    fileName: "linehaul_request.xlsx",
                  })
                }
                className={`w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                  showCheckboxes && selectedRows.length === 0
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                title={t("Export to Excel")}
              >
                <IconDownload size={17} stroke={2.5} />
                {selectedRows.length > 0 && (
                  <span className="ml-1 text-xs font-bold">({selectedRows.length})</span>
                )}
              </button>

              {/* Entries limit selector */}
              {data && data.totalPages * limit > 10 && (
                <EntriesSelector
                  value={limit}
                  options={[
                    10,
                    ...(data.totalPages * limit > 10 ? [20] : []),
                    ...(data.totalPages * limit > 20 ? [30] : []),
                    ...(data.totalPages * limit > 50 ? [50] : []),
                    ...(data.totalPages * limit > 100 ? [100] : []),
                  ]}
                  onChange={(val) => {
                    setLimit(val);
                    setPageNo(1);
                  }}
                />
              )}

              {/* Columns Filter */}
              <ColumnsFilter
                columnsData={columnsData.filter((c) => c.value !== "checkbox").map((c) => ({ ...c, text: t(c.text) }))}
                setHeadings={setHeadings}
              />

              {/* Dropdown Filter */}
              <DropdownFilter
                filterheadings={[]}
                triggerClassName="border border-slate-200 bg-white rounded-lg cursor-pointer text-[#212121] hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 h-8 w-8 flex items-center justify-center shadow-sm select-none"
                iconSize={17}
              />
            </div>
          </div>

          {/* Table */}
          <div className="border-t border-slate-100">
            {isLoading || isFetching ? (
              <TableSkeleton
                columns={columnsData.filter((c) => c.isShow).map((c) => t(c.text))}
                rows={6}
              />
            ) : data?.results?.length ? (
              <ListView
                data={data?.results ?? []}
                onRowClick={(client) => console.log("Clicked:", client)}
                columns={columns}
                mainDivClassName="bg-white"
                mailDivClassName="p-0"
              />
            ) : (
              <NoDataFound />
            )}
          </div>
        </div>

        <div className="flex justify-center items-center py-4">
          {data?.totalPages > 1 && (
            <Pagination
              currentPage={pageNo}
              totalPages={data.totalPages}
              fetchData={handlePaginationFetch}
            />
          )}
        </div>
      </div>

      <RequestViewApproveModal
        open={isOpen}
        onClose={closeModal}
        request={selectedRequest}
        onApproved={() => refetch()}
      />
    </div>
  );
};

export default LinehualTab;
